import { useMemo } from 'react';

import useTeacherDashboardFeatures from 'core/chatbot/hooks/useTeacherDashboardFeatures';
import { filterRoutesByFeatures } from 'core/utils/filterRoutesByFeatures';

import type { RouteConfig } from 'app/types/router';

/**
 * Routes with tenant feature gating already applied. Shared by the app and
 * edelvives routers, each passing the private routes of its own routesConfig.
 * Routes tagged with `feature:` in routesConfig are dropped when the
 * tenant flag is explicitly false (fail-open otherwise).
 *
 * Memoized on `features`: `filterRoutesByFeatures` returns a new array on every call, and the
 * result feeds `RouteChangeHandler`, whose effect depends on the route list (TAN-8655). Without
 * this the effect would re-run on every render of the router. `features` is a safe dependency:
 * `useTeacherDashboardFeatures` is built on `createSessionConfigHook`, which keeps one reference
 * per value (the module-level defaults while loading, the cached object once fetched).
 */
export function useFilteredRoutes(routes: RouteConfig[]): RouteConfig[] {
  const { features } = useTeacherDashboardFeatures();

  return useMemo(() => filterRoutesByFeatures(routes, features), [routes, features]);
}
